import PropTypes from 'prop-types';
import { combineReducers } from 'redux';
import { globalizeSelectors } from '../Utils';
import { singleDataType, createSingleDataReducer } from '../factories/ApiCall';
import type {
  ReplicationAction,
  ReplicationDetailsResponse,
  ReplicationFullSyncPayload,
  ReplicationMessageResponse,
  ReplicationModalState,
  ReplicationRunActionPayload,
  ReplicationState,
} from '../types/Replication';

/*
 * action types
 */

const types = {
  REPLICATION_FETCH_DETAILS: 'REPLICATION/FETCH_DETAILS',
  REPLICATION_RUNACTION: 'REPLICATION/RUNACTION',
  REPLICATION_TOGGLE_MODAL: 'REPLICATION/REPLICATION_TOGGLE_MODAL',
  REPLICATION_START_STATUS_CHECKING:
    'REPLICATION/REPLICATION_START_STATUS_CHECKING',
  REPLICATION_SET_FULL_SYNC_SCHEDULE:
    'REPLICATION/REPLICATION_SET_FULL_SYNC_SCHEDULE',
  REPLICATION_RESEND_ORDERS: 'REPLICATION/RESEND_ORDERS',
} as const;

const mountPath = 'replication'; // mount point in global state, must match root reducer

/*
 * action creators
 */

const actions = {
  fetchReplicationDetails: (): ReplicationAction => ({
    type: types.REPLICATION_FETCH_DETAILS,
  }),
  runReplicationAction: (
    action: ReplicationRunActionPayload
  ): ReplicationAction<ReplicationRunActionPayload> => ({
    type: types.REPLICATION_RUNACTION,
    payload: action,
  }),
  resendOrders: (): ReplicationAction => ({
    type: types.REPLICATION_RESEND_ORDERS,
  }),
  startStatusChecking: (): ReplicationAction => ({
    type: types.REPLICATION_START_STATUS_CHECKING,
  }),
  toggleModal: (): ReplicationAction => ({
    type: types.REPLICATION_TOGGLE_MODAL,
  }),
  setFullSyncSchedule: (
    state: ReplicationFullSyncPayload['state'],
    executiontime?: ReplicationFullSyncPayload['executiontime']
  ): ReplicationAction<ReplicationFullSyncPayload> => ({
    type: types.REPLICATION_SET_FULL_SYNC_SCHEDULE,
    payload: { state, executiontime },
  }),
};

/*
 * state shape
 */
const propType = PropTypes.shape({
  details: singleDataType,
  action_results: singleDataType,
  schedule_set: singleDataType,
  resend_orders: singleDataType,
});

/*
 * reducer
 */

const initialState: ReplicationModalState = {
  open: false,
};

function modalReducer(
  state: ReplicationModalState = initialState,
  action: ReplicationAction
): ReplicationModalState {
  switch (action.type) {
    case types.REPLICATION_TOGGLE_MODAL: {
      return { ...state, open: !state.open };
    }
    default: {
      return state;
    }
  }
}

const reducer = combineReducers({
  details: createSingleDataReducer<
    ReplicationDetailsResponse,
    Record<string, never>
  >(types.REPLICATION_FETCH_DETAILS, false),
  action_results: createSingleDataReducer<
    ReplicationMessageResponse,
    ReplicationRunActionPayload
  >(types.REPLICATION_RUNACTION),
  schedule_set: createSingleDataReducer<
    ReplicationMessageResponse,
    ReplicationFullSyncPayload
  >(types.REPLICATION_SET_FULL_SYNC_SCHEDULE),
  resend_orders: createSingleDataReducer<ReplicationMessageResponse>(
    types.REPLICATION_RESEND_ORDERS
  ),
  modal: modalReducer,
}) as (
  state: ReplicationState | undefined,
  action: ReplicationAction
) => ReplicationState;

/*
 * selectors
 */
const localSelectors = {
  details: (state: ReplicationState): ReplicationState['details'] =>
    state.details,
  action_results: (
    state: ReplicationState
  ): ReplicationState['action_results'] => state.action_results,
};

const selectors = globalizeSelectors(localSelectors, mountPath);

export {
  types as actionTypes,
  actions as default,
  propType,
  selectors,
  reducer,
};
